import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import { Android, MoreVert, Replay } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import AiTicketControls from "../AiTicketControls";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(1),
		},
	},
}));

const TicketActionButtons = ({ ticket, onUpdateTicket, stayHere }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [aiLoading, setAiLoading] = useState(false);
	const [aiEnabled, setAiEnabled] = useState(null);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		let mounted = true;
		const fetchAiState = async () => {
			if (!ticket?.id) return;
			try {
				const { data } = await api.get(`/tickets/${ticket.id}/ai-conversation`);
				if (mounted) setAiEnabled(Boolean(data.enabled));
			} catch (_) {
				if (mounted) setAiEnabled(null);
			}
		};
		fetchAiState();
		return () => {
			mounted = false;
		};
	}, [ticket?.id]);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (stayHere) {
				try {
					const { data } = await api.get(`/tickets/${ticket.id}`);
					if (onUpdateTicket) onUpdateTicket(data);
				} catch (fetchErr) {
					toastError(fetchErr);
				}
				return;
			}
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleToggleAiConversation = async () => {
		if (!ticket?.id || aiEnabled === null) return;
		setAiLoading(true);
		try {
			const { data } = await api.post(`/tickets/${ticket.id}/ai-conversation`, {
				enabled: !aiEnabled,
			});
			setAiEnabled(Boolean(data.enabled));
		} catch (err) {
			toastError(err);
		} finally {
			setAiLoading(false);
		}
	};

	const aiToggleButton = ticket?.id ? (
		<Tooltip
			title={
				aiEnabled === false
					? "Ativar IA nesta conversa"
					: "Desativar IA nesta conversa"
			}
		>
			<span>
				<ButtonWithSpinner
					loading={aiLoading}
					startIcon={<Android />}
					size="small"
					color={aiEnabled === false ? "default" : "primary"}
					variant={aiEnabled === false ? "outlined" : "contained"}
					disabled={aiEnabled === null || aiLoading}
					onClick={handleToggleAiConversation}
				>
					{aiEnabled === false ? "IA off" : "IA on"}
				</ButtonWithSpinner>
			</span>
		</Tooltip>
	) : null;

	return (
		<div className={classes.actionButtons}>
			<AiTicketControls ticket={ticket} />
			{ticket.status === "closed" && (
				<>
					{aiToggleButton}
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.reopen")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
            onUpdateTicket={onUpdateTicket}
					/>
				</>
			)}
			{ticket.status === "open" && (
				<>
					{aiToggleButton}
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
            onUpdateTicket={onUpdateTicket}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<>
					{aiToggleButton}
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.accept")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
            onUpdateTicket={onUpdateTicket}
					/>
				</>
			)}
		</div>
	);
};

export default TicketActionButtons;
